import { parseBool } from "shared/Helpers/lang"
import { CanteenItem, IMonetaryAmount, IUser, UserLocation, UserSetting, UserSettingElementType } from "shared/Types/appTypes"
import { isPublicLocation } from "../Location/locationLib"
import { AccountType } from "./userTypes"

interface UserState {
    user: IUser | null
}

export function selectUser({ user }: UserState): IUser | null {
    return user
}

export function selectUserGivenName({ user }: UserState): string | undefined {
    return user?.givenName
}

export function selectUserEmail({ user }: UserState): string | undefined {
    return user?.email ?? user?.username
}

export function selectUserLocation({ user }: UserState): UserLocation | undefined {
    return user?.location
}

export function selectUserLocationName({ user }: UserState): string | undefined {
    if (user?.location && !user.location.selectLocation) {
        return user.isPrivateAccount ? user.location.kitchen?.name : user.location.name
    }

    return undefined
}

export function selectUserCanteen({ user }: UserState): CanteenItem | undefined {
    return user?.location && !user.location.selectLocation ? user.location.kitchen : undefined
}

export function selectUserLocationId({ user }: UserState): number | undefined {
    return user?.location && !user.location.selectLocation && "id" in user.location ? user.location.id : undefined
}

export function selectUserCanteenId({ user }: UserState): number | undefined {
    return user?.location && !user.location.selectLocation ? user.location.kitchen?.id : undefined
}

export function selectUserMustPickLocation({ user }: UserState): boolean {
    // If user or location is missing, do not require user to pick location
    // Otherwise selectLocation determines if location should be picked
    // See: https://facilitynet.monday.com/boards/531833481/pulses/541065170
    return user?.location?.selectLocation ?? false
}

export function selectUserAccountBalance({ user }: UserState): IMonetaryAmount | undefined {
    return user?.accountBalance
}

export function selectUserIsPrivate({ user }: UserState): boolean {
    return user?.isPrivateAccount ?? false
}

export function selectUserAccountType(state: UserState): AccountType {
    return selectUserIsPrivate(state) ? AccountType.PRIVATE_ACCOUNT : AccountType.COMPANY_ACCOUNT
}

export function selectUserSettings({ user }: UserState): UserSetting[] | undefined {
    return user?.userSettings
}

export function selectUserLocale({ user }: UserState): string | undefined {
    return user?.userSettings.find((setting) => setting.name === "LANGUAGE_CODE")?.value
}

export function selectUserIntercomConsent({ user }: UserState) {
    const setting = user?.userSettings.find((setting) => setting.name === "INTERCOM_CONSENT")
    return parseBool(setting?.value ?? "") ?? false
}

export const ACCEPT_PUSH_NOTIFICATIONS_SETTING = "ACCEPT_PUSH_NOTIFICATIONS" as const
export const ACCEPT_PUSH_FOR_TX_SETTING = "ACCEPT_PUSH_FOR_TX" as const
export const ACCEPT_PUSH_FOR_NEWS_SETTING = "ACCEPT_PUSH_FOR_NEWS" as const

export type PushNotificationSettings = Readonly<{
    // undefined: the user has not answered the permission request yet
    accept: boolean | undefined
    forTx: boolean | undefined
    forNews: boolean | undefined
}>

function selectBoolSetting({ user }: UserState, name: string): boolean | undefined {
    const setting = user?.userSettings.find((setting) => setting.name === name)
    if (!setting) return undefined
    return parseBool(setting.value) ?? undefined
}

// TODO: Use createSelector here
export function selectPushNotificationSettings(state: UserState): PushNotificationSettings {
    return {
        accept: selectBoolSetting(state, ACCEPT_PUSH_NOTIFICATIONS_SETTING),
        forTx: selectBoolSetting(state, ACCEPT_PUSH_FOR_TX_SETTING),
        forNews: selectBoolSetting(state, ACCEPT_PUSH_FOR_NEWS_SETTING),
    }
}

export function selectAcceptPushNotifications(state: UserState): boolean {
    return selectBoolSetting(state, ACCEPT_PUSH_NOTIFICATIONS_SETTING) ?? false
}

// Push notification settings have their own sub page instead of plain toggles
const PUSH_SETTING_PREFIX = "ACCEPT_PUSH_"

const LEGACY_RECEIPTS_SETTING = "ORDER_EMAIL_RECEIPT"
const RECEIPT_TOGGLES = [
    { name: "ORDER_EMAIL_RECEIPT_MEETING_SERVICE", translationKey: "settings-receipts-meeting-service" },
    { name: "ORDER_EMAIL_RECEIPT_CANTEEN", translationKey: "settings-receipts-canteen" },
]

const isLegacyReceiptToggle = ({ name }: UserSetting) => name === LEGACY_RECEIPTS_SETTING
const isReceiptToggle = ({ name }: UserSetting) => RECEIPT_TOGGLES.some((toggle) => toggle.name === name)

function splitReceiptToggles(settings: UserSetting[]): UserSetting[] {
    const source = settings.find(isLegacyReceiptToggle) ?? settings.find(isReceiptToggle)
    if (!source) return settings

    // Saved ones come back without elementType; unsaved ones start from the old toggle
    const receipts = RECEIPT_TOGGLES.map((toggle) => ({
        ...toggle,
        elementType: UserSettingElementType.TOGGLE,
        value: settings.find(({ name }) => name === toggle.name)?.value ?? source.value,
    }))

    return [...receipts, ...settings.filter((setting) => !isLegacyReceiptToggle(setting) && !isReceiptToggle(setting))]
}

export function toPlainToggleSettings(settings: UserSetting[] = []): UserSetting[] {
    return splitReceiptToggles(settings).filter(
        (setting) => setting.elementType === UserSettingElementType.TOGGLE && !setting.name.startsWith(PUSH_SETTING_PREFIX),
    )
}
